import React, { useState, useEffect } from 'react';
import { useDrag } from '@use-gesture/react';
import { TextBlock } from './imageTranslatorTypes.types';
import WebFont from 'webfontloader';
import { ColorPicker } from './ColorPicker';

const FONT_FAMILIES = [
  'Arial',
  'Roboto',
  'Open Sans',
  'Lato',
  'Playpen Sans',
  'Montserrat',
  'Source Sans Pro',
  'Racing Sans One',
  'Inria Serif',
  'Reggae One',
  'Fugaz One',
  'Merienda',
  'Rubik Dirt',
  'Emblema One'
];

const FONT_WEIGHTS = [
  { value: '100', label: 'Thin 100' },
  { value: '300', label: 'Light 300' },
  { value: '400', label: 'Normal 400' },  
  { value: '500', label: 'Medium 500' },
  { value: '700', label: 'Bold 700' },
  { value: '900', label: 'Black 900' },
];

const ALIGN_OPTIONS = [
  { value: 'left', label: 'Left' },
  { value: 'center', label: 'Center' },
  { value: 'right', label: 'Right' },
];

const VERTICAL_ALIGN_OPTIONS = [
  { value: 'top', label: 'Top' },
  { value: 'middle', label: 'Middle' },
  { value: 'bottom', label: 'Bottom' },
];

const FONT_STYLES = [
  { value: 'normal', label: 'Normal' },
  { value: 'italic', label: 'Italic' },
];

interface EditableTextOptionsProps {
  imageCanvasRef: any;
  blocks: TextBlock[];
  commonValues: Partial<TextBlock> | null;
  position: { x: number; y: number };
  onDrag: (position: { x: number; y: number }) => void;
  onUpdate: (updates: Partial<TextBlock>) => void;
  onUpdateBlock: (blockId: string, updates: Partial<TextBlock>) => void;
  onDelete: () => void;
  onPickColor: (callback: (color: string) => void) => void;
  className?: string;
}

interface ColorPickerState {
  isOpen: boolean;
  type: string;
  position: { x: number; y: number };
  isPickingColor: boolean;
}

// Add a new CSS class to prevent text selection
const noSelectClass = "select-none";

const LOADED_FONTS = new Set<string>(['Arial']);

export const EditableTextOptions: React.FC<EditableTextOptionsProps> = ({
  imageCanvasRef,
  blocks,
  commonValues,
  position,
  onDrag,
  onUpdate,
  onUpdateBlock,
  onDelete,
  onPickColor,
  className = ''
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  const [colorPicker, setColorPicker] = useState<ColorPickerState>({
    isOpen: false,
    type: '',
    position: { x: 0, y: 0 },
    isPickingColor: false
  });
  const [maxHeight, setMaxHeight] = useState('auto');

  // Load fonts when needed
  useEffect(() => {
    const loadGoogleFont = async (fontFamily: string) => {
      if (LOADED_FONTS.has(fontFamily) || fontFamily === 'Arial') return;

      WebFont.load({
        google: {
          families: [
            `${fontFamily}:100,300,400,500,700,900`,
            `${fontFamily}:100italic,300italic,400italic,500italic,700italic,900italic`
          ]
        },
        active: () => {
          console.log('Font loaded:', fontFamily);
          LOADED_FONTS.add(fontFamily);
        },
        inactive: () => {
          console.error('Failed to load font:', fontFamily);
        }
      });
    };

    // Load fonts for all blocks
    blocks.forEach(block => {
      if (block.fontFamily && !LOADED_FONTS.has(block.fontFamily)) {
        loadGoogleFont(block.fontFamily);
      }
    });
  }, [blocks]);

  // Add effect to calculate max height
  useEffect(() => {
    const updateMaxHeight = () => {
      const windowHeight = window.innerHeight;
      const padding = 40; // Space from top and bottom of window
      setMaxHeight(`${windowHeight - padding}px`);
    };

    updateMaxHeight();
    window.addEventListener('resize', updateMaxHeight);
    return () => window.removeEventListener('resize', updateMaxHeight);
  }, []);

  const handleColorPickerOpen = (type: string, buttonElement: HTMLButtonElement) => {
    const boundingRect = buttonElement.getBoundingClientRect();
    const optionsPanel = buttonElement.closest('.options-panel');
    
    if (!optionsPanel) return;

    const windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;
    
    // Color picker dimensions
    const colorPickerHeight = 307;
    const colorPickerWidth = 220;
    
    // Calculate available space in different directions
    const spaceBelow = windowHeight - boundingRect.bottom;
    const spaceAbove = boundingRect.top;
    const spaceRight = windowWidth - boundingRect.right;
    const spaceLeft = boundingRect.left;
    
    // Determine optimal position
    let x = boundingRect.left;
    let y = boundingRect.bottom + 10; // Default position (below with 10px gap)

    // Check if we need to show above instead of below
    if (spaceBelow < colorPickerHeight && spaceAbove > spaceBelow) {
      y = boundingRect.top - colorPickerHeight - 10;
    }

    // Adjust horizontal position if it would go off-screen
    if (spaceRight < colorPickerWidth && spaceLeft > spaceRight) {
      x = boundingRect.right - colorPickerWidth;
    }

    // Make sure picker doesn't go off-screen
    x = Math.max(10, Math.min(x, windowWidth - colorPickerWidth - 10));
    y = Math.max(10, Math.min(y, windowHeight - colorPickerHeight - 10));

    setColorPicker({
      isOpen: true,
      type,
      position: { x, y },
      isPickingColor: false
    });
  };

  const handleColorPickerClose = () => {
    setColorPicker(prev => ({ ...prev, isOpen: false }));
  };

  const handlePickColor = (type: string) => {
    setColorPicker(prev => ({
      ...prev,
      type,
      isPickingColor: true
    }));
    onPickColor((color: string) => {
      handleColorChange(type, color);
      setColorPicker(prev => ({ ...prev, isPickingColor: false }));
    });
  };

  const handleColorChange = (type: string, color: string, opacity?: number) => {
    switch (type) {
      case 'Text Color':
        onUpdate({ 
          color,
          textOpacity: opacity 
        });
        break;
      case 'Background Color':
        onUpdate({ 
          backgroundColor: color,
          backgroundOpacity: opacity 
        });
        break;
      case 'Text Border':
        onUpdate({ 
          textBorder: {
            ...commonValues?.textBorder,
            color,
            width: commonValues?.textBorder?.width || 1
          }
        });
        break;
      case 'Box Shadow':
        onUpdate({ 
          boxShadow: {
            ...commonValues?.boxShadow,
            color,
            opacity: opacity || 0.5,
            blur: commonValues?.boxShadow?.blur || 0,
            offsetX: commonValues?.boxShadow?.offsetX || 0,
            offsetY: commonValues?.boxShadow?.offsetY || 0
          }
        });
        break;
      case 'Background Border':
        onUpdate({ 
          backgroundBorder: {
            ...commonValues?.backgroundBorder,
            color,
            width: commonValues?.backgroundBorder?.width || 1
          }
        });
        break;
    }
  };

  const bindDragHandle = useDrag(({ movement: [x, y], first, last }) => {
    if (first) {
      setIsDragging(true);
    }
    if (last) {
      setIsDragging(false);
      onDrag({
        x: position.x + x,
        y: position.y + y
      });
    } else {
      setDragPosition({ x, y });
    }
  });

  const style = {
    position: 'fixed' as const,
    top: 20,
    left: 0,
    transform: isDragging
      ? `translate(${position.x + dragPosition.x}px, ${position.y + dragPosition.y}px)`
      : `translate(${position.x}px, ${position.y}px)`,
    touchAction: 'none',
    zIndex: 1000,
    maxHeight,
    display: 'flex',
    flexDirection: 'column' as const,
  };

  const handleRestorePosition = () => {
    if (!commonValues) return;
    
    onUpdate({
      x: commonValues.x,
      y: commonValues.y,
      width: commonValues.width,
      height: commonValues.height,
      rotation: 0 // Reset rotation as well
    });
  };

  const handleCenterHorizontally = () => {
    if (!imageCanvasRef.current || blocks.length === 0) return;
    
    const imageDimensions = imageCanvasRef.current.getImageDimensions();
    if (!imageDimensions) return;
    
    const imageWidth = imageDimensions.width;
    
    // Center each block individually based on its own width
    blocks.forEach(block => {
      const centeredX = (imageWidth - block.width) / 2;
      onUpdateBlock(block.id, { x: centeredX });
    });
  };

  return (
    <div style={style} className={`fixed bg-white rounded-lg shadow-lg w-80 z-50 options-panel ${noSelectClass}`}>
      <div 
        {...bindDragHandle()}
        className="h-6 bg-gray-100 rounded-t-lg cursor-grab hover:bg-gray-200 flex items-center justify-center shrink-0"
      >
        <div className="w-8 h-1 bg-gray-300 rounded-full" />
      </div>

      <div className="bg-white rounded-b-lg shadow-lg p-4 overflow-y-auto">
        <div className="mb-4 text-sm text-gray-600">
          {blocks.length > 1 ? (
            <span>{blocks.length} text blocks selected</span>
          ) : (
            <span>1 text block selected</span>
          )}
        </div>

        <button
          onClick={onDelete}
          className="w-full px-3 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-sm flex items-center justify-center gap-2 mb-4"
        >
          <svg 
            className="w-4 h-4" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" 
            />
          </svg>
          Delete {blocks.length > 1 ? 'Selected Blocks' : 'Text Block'}
        </button>
        {commonValues && (
          <>
            {/* <button
              onClick={handleRestorePosition}
              className="w-full px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-sm flex items-center justify-center gap-2"
            >
              <svg 
                className="w-4 h-4" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" 
                />
              </svg>
              Restore Position & Size
            </button> */}
            <button
              onClick={handleCenterHorizontally}
              className="w-full px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-sm flex items-center justify-center gap-2"
            >
              <svg 
                className="w-4 h-4" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" 
                />
              </svg>
              Center Horizontally
            </button>
          </>
        )}
        
        {/* Font Family */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Font Family
          </label>
          <select
            value={commonValues?.fontFamily || ''}
            onChange={(e) => onUpdate({ fontFamily: e.target.value })}
            className="w-full border rounded-sm px-3 py-2"
          >
            {!commonValues?.fontFamily && (
              <option value="">Multiple values</option>
            )}
            {FONT_FAMILIES.map(font => (
              <option key={font} value={font} style={{ fontFamily: font }}>
                {font}
              </option>
            ))}
          </select>
        </div>

        {/* Font Weight */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Font Weight
          </label>
          <select
            value={commonValues?.fontWeight || ''}
            onChange={(e) => onUpdate({ fontWeight: e.target.value })}
            className="w-full border rounded-sm px-3 py-2"
          >
            {!commonValues?.fontWeight && (
              <option value="">Multiple values</option>
            )}
            {FONT_WEIGHTS.map(weight => (
              <option key={weight.value} value={weight.value}>
                {weight.label}
              </option>
            ))}
          </select>
        </div>

        {/* Font Size */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Font Size
          </label>
          <input
            type="number"
            value={commonValues?.fontSize ?? ''}
            onChange={(e) => onUpdate({ fontSize: Number(e.target.value) })}
            className="w-full border rounded-sm px-3 py-2"
            placeholder={commonValues?.fontSize === undefined ? 'Multiple values' : undefined}
          />
        </div>

        {/* Text Color */}
        <ColorPicker
          label="Text Color"
          color={commonValues?.color || '#000000'}
          opacity={commonValues?.textOpacity}
          isOpen={colorPicker.isOpen && colorPicker.type === 'Text Color'}
          position={colorPicker.position}
          onOpen={(button) => handleColorPickerOpen('Text Color', button)}
          onClose={handleColorPickerClose}
          onChange={(color, opacity) => handleColorChange('Text Color', color, opacity)}
          onPickColor={() => handlePickColor('Text Color')}
          isPickingColor={colorPicker.isPickingColor && colorPicker.type === 'Text Color'}
          showMultipleValues={!commonValues?.color}
        />

        {/* Background Color */}
        <ColorPicker
          label="Background Color"
          color={commonValues?.backgroundColor || '#ffffff'}
          opacity={commonValues?.backgroundOpacity}
          isOpen={colorPicker.isOpen && colorPicker.type === 'Background Color'}
          position={colorPicker.position}
          onOpen={(button) => handleColorPickerOpen('Background Color', button)}
          onClose={handleColorPickerClose}
          onChange={(color, opacity) => handleColorChange('Background Color', color, opacity)}
          onPickColor={() => handlePickColor('Background Color')}
          isPickingColor={colorPicker.isPickingColor && colorPicker.type === 'Background Color'}
          showMultipleValues={!commonValues?.backgroundColor}
        />

        {/* Padding */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Padding
          </label>
          <input
            type="number"
            min="0"
            max="50"
            value={commonValues?.padding || 0}
            onChange={(e) => onUpdate({ padding: Number(e.target.value) })}
            className="w-full border rounded-sm px-3 py-2"
            placeholder={commonValues?.padding === undefined ? 'Multiple values' : undefined}
          />
        </div>

        {/* Corner Radius */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Corner Radius
          </label>
          <input
            type="number"
            min="0"
            max="100"
            value={commonValues?.cornerRadius || 0}
            onChange={(e) => onUpdate({ cornerRadius: Number(e.target.value) })}
            className="w-full border rounded-sm px-3 py-2"
            placeholder={commonValues?.cornerRadius === undefined ? 'Multiple values' : undefined}
          />
        </div>

        {/* Text Alignment */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Text Align
          </label>
          <select
            value={commonValues?.align || 'left'}
            onChange={(e) => onUpdate({ align: e.target.value as TextBlock['align'] })}
            className="w-full border rounded-sm px-3 py-2"
          >
            {ALIGN_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Vertical Alignment */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Vertical Align
          </label>
          <select
            value={commonValues?.verticalAlign || 'top'}
            onChange={(e) => onUpdate({ verticalAlign: e.target.value as TextBlock['verticalAlign'] })}
            className="w-full border rounded-sm px-3 py-2"
          >
            {VERTICAL_ALIGN_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Font Style */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Font Style
          </label>
          <select
            value={commonValues?.fontStyle || 'normal'}
            onChange={(e) => onUpdate({ fontStyle: e.target.value as TextBlock['fontStyle'] })}
            className="w-full border rounded-sm px-3 py-2"
          >
            {FONT_STYLES.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Text Border */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Text Border
          </label>
          <div className="grid grid-cols-2 gap-2">
            <ColorPicker
              label="Border Color"
              color={commonValues?.textBorder?.color || '#000000'}
              opacity={1}
              isOpen={colorPicker.isOpen && colorPicker.type === 'Text Border'}
              position={colorPicker.position}
              onOpen={(button) => handleColorPickerOpen('Text Border', button)}
              onClose={handleColorPickerClose}
              onChange={(color) => handleColorChange('Text Border', color)}
              onPickColor={() => handlePickColor('Text Border')}
              isPickingColor={colorPicker.isPickingColor && colorPicker.type === 'Text Border'}
              showMultipleValues={!commonValues?.textBorder?.color}
            />
            <input
              type="number"
              min="0"
              max="20"
              value={commonValues?.textBorder?.width || 0}
              onChange={(e) => onUpdate({
                textBorder: {
                  ...commonValues?.textBorder,
                  color: commonValues?.textBorder?.color || '#000000',
                  width: Number(e.target.value)
                }
              })}
              className="w-full border rounded-sm px-3 py-2"
              placeholder={commonValues?.textBorder?.width === undefined ? 'Multiple values' : undefined}
            />
          </div>
        </div>

        {/* Box Shadow */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Box Shadow
          </label>
          <div className="space-y-2">
            <ColorPicker
              label="Shadow Color"
              color={commonValues?.boxShadow?.color || '#000000'}
              opacity={commonValues?.boxShadow?.opacity || 0.5}
              isOpen={colorPicker.isOpen && colorPicker.type === 'Box Shadow'}
              position={colorPicker.position}
              onOpen={(button) => handleColorPickerOpen('Box Shadow', button)}
              onClose={handleColorPickerClose}
              onChange={(color, opacity) => handleColorChange('Box Shadow', color, opacity)}
              onPickColor={() => handlePickColor('Box Shadow')}
              isPickingColor={colorPicker.isPickingColor && colorPicker.type === 'Box Shadow'}
              showMultipleValues={!commonValues?.boxShadow?.color}
            />
            <div className="grid grid-cols-3 gap-2">
              <input
                type="number"
                value={commonValues?.boxShadow?.blur || 0}
                onChange={(e) => onUpdate({
                  boxShadow: {
                    ...commonValues?.boxShadow,
                    blur: Number(e.target.value),
                    opacity: commonValues?.boxShadow?.opacity || 0.5,
                    offsetX: commonValues?.boxShadow?.offsetX || 0,
                    offsetY: commonValues?.boxShadow?.offsetY || 0,
                    color: commonValues?.boxShadow?.color || '#000000'
                  }
                })}
                className="w-full border rounded-sm px-3 py-2"
                placeholder={commonValues?.boxShadow?.blur === undefined ? 'Multiple values' : 'Blur'}
              />
              <input
                type="number"
                value={commonValues?.boxShadow?.offsetX || 0}
                onChange={(e) => onUpdate({
                  boxShadow: {
                    ...commonValues?.boxShadow,
                    offsetX: Number(e.target.value),
                    opacity: commonValues?.boxShadow?.opacity || 0.5,
                    blur: commonValues?.boxShadow?.blur || 0,
                    offsetY: commonValues?.boxShadow?.offsetY || 0,
                    color: commonValues?.boxShadow?.color || '#000000'
                  }
                })}
                className="w-full border rounded-sm px-3 py-2"
                placeholder={commonValues?.boxShadow?.offsetX === undefined ? 'Multiple values' : 'X Offset'}
              />
              <input
                type="number"
                value={commonValues?.boxShadow?.offsetY || 0}
                onChange={(e) => onUpdate({
                  boxShadow: {
                    ...commonValues?.boxShadow,
                    offsetY: Number(e.target.value),
                    opacity: commonValues?.boxShadow?.opacity || 0.5,
                    blur: commonValues?.boxShadow?.blur || 0,
                    offsetX: commonValues?.boxShadow?.offsetX || 0,
                    color: commonValues?.boxShadow?.color || '#000000'
                  }
                })}
                className="w-full border rounded-sm px-3 py-2"
                placeholder={commonValues?.boxShadow?.offsetY === undefined ? 'Multiple values' : 'Y Offset'}
              />
            </div>
          </div>
        </div>

        {/* Background Border */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Background Border
          </label>
          <div className="grid grid-cols-2 gap-2">
            <ColorPicker
              label="Border Color"
              color={commonValues?.backgroundBorder?.color || '#000000'}
              opacity={1}
              isOpen={colorPicker.isOpen && colorPicker.type === 'Background Border'}
              position={colorPicker.position}
              onOpen={(button) => handleColorPickerOpen('Background Border', button)}
              onClose={handleColorPickerClose}
              onChange={(color) => handleColorChange('Background Border', color)}
              onPickColor={() => handlePickColor('Background Border')}
              isPickingColor={colorPicker.isPickingColor && colorPicker.type === 'Background Border'}
              showMultipleValues={!commonValues?.backgroundBorder?.color}
            />
            <input
              type="number"
              min="0"
              max="20"
              value={commonValues?.backgroundBorder?.width || 0}
              onChange={(e) => onUpdate({
                backgroundBorder: {
                  ...commonValues?.backgroundBorder,
                  color: commonValues?.backgroundBorder?.color || '#000000',
                  width: Number(e.target.value)
                }
              })}
              className="w-full border rounded-sm px-3 py-2"
              placeholder={commonValues?.backgroundBorder?.width === undefined ? 'Multiple values' : undefined}
            />
          </div>
        </div>

        {/* Line Height */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Line Height
          </label>
          <div className="flex items-center gap-2">
            <input
              type="range"
              min="0.5"
              max="3"
              step="0.1"
              value={commonValues?.lineHeight || 1}
              onChange={(e) => onUpdate({ lineHeight: Number(e.target.value) })}
              className="flex-1"
            />
            <input
              type="number"
              min="0.5"
              max="3"
              step="0.1"
              value={commonValues?.lineHeight || 1}
              onChange={(e) => onUpdate({ lineHeight: Number(e.target.value) })}
              className="w-20 border rounded-sm px-3 py-2"
              placeholder={commonValues?.lineHeight === undefined ? 'Multiple values' : undefined}
            />
          </div>
        </div>
      </div>
    </div>
  );
}; 